import store from 'store';
import mapConfig from '@/pages/mapView/components/map/map.config';
import vuex from '@/store';
import Utils from './Utils';
import { i18n } from '@/main';

export enum Types {
  DEVICE_CLASSES = 'deviceclasses',
  PROPS = 'properties',
  DEVICES = 'devices',
  COMPANIES = 'companies',
  PROJECTS = 'projects'
}

export enum Methods {
  GET = 'get',
  PUT = 'put',
  POST = 'post',
  HEAD = 'head',
  PATCH = 'patch',
  DELETE = 'delete'
}

export enum DataMethods {
  QUERY = 'query',
  OBJECT = 'object',
  FORMDATA = 'formData'
}

export interface Parameters {
  auth: string;
  apiURL?: string;
  apiKey?: string;
  serialize: boolean;
  dataMethod: string;
  contentType: string;
  itemID?: string;
  query: any;
  report: boolean;
  headers?: any;
}

export default class API {
  public static readonly API_URL = process.env.VUE_APP_HTTP_SERVER;
  public static readonly DASHBOARD_API_URL = process.env.VUE_APP_DASHBOARD_API;
  public static readonly BACKEND_SERVICES_URL = process.env.VUE_APP_BACKEND_SERVICES_API;

  public static getAuthHeaders(): Record<string, string> {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.jwt_token) {
      return { Authorization: `Bearer ${user.jwt_token}` };
    }
    return {};
  }

  public static isAuthError(detail: string): boolean {
    const lowerDetail = detail.toLowerCase();
    return lowerDetail.includes('apikey') || lowerDetail.includes('token');
  }

  public static get(type, path?, data?, params?, version?) {
    return this.knock(type, path, data, Methods.GET, params, version);
  }

  public static put(type, path?, data?, params?, version?) {
    return this.knock(type, path, data, Methods.PUT, params, version);
  }

  public static post(type, path?, data?, params?, version?) {
    return this.knock(type, path, data, Methods.POST, params, version);
  }

  public static streamingPost(type, path?, data?, params?, version?) {
    return this.knockStream(type, path, data, params, version);
  }

  public static head(type, path?, data?, params?, version?) {
    return this.knock(type, path, data, Methods.HEAD, params, version);
  }

  public static patch(type, path?, data?, params?, version?) {
    return this.knock(type, path, data, Methods.PATCH, params, version);
  }

  public static delete(type, path?, data?, params?, version?) {
    return this.knock(type, path, data, Methods.DELETE, params, version);
  }

  public static async dashboardAPI(url, method, headers, body) {
    return API.externalAPI(`${API.DASHBOARD_API_URL}${url}`, method, headers, body);
  }

  public static async backendServicesAPI(url, method, headers, body) {
    return API.externalAPI(`${API.BACKEND_SERVICES_URL}${url}`, method, headers, body);
  }

  public static async externalAPI(url, method, headers, body) {
    if ((vuex.state as any).Global.show_not_allowed_user_dialog) {
      return;
    }

    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...this.getAuthHeaders(),
        ...headers
      }
    };

    if (method !== 'GET') {
      options['body'] = JSON.stringify(body);
    }

    try {
      const response = await fetch(url, options);
      if (response && response.status === 403) {
        try {
          const payload = await response.clone().json();
          const detail = payload && payload.detail ? payload.detail : '';
          if (API.isAuthError(detail)) {
            Utils.handleNotAllowedUser();
          } else {
            vuex.dispatch('Global/throwNotify', {
              type: 'error',
              title: `${i18n.t('Error')}!`,
              text: i18n.t(payload.detail.replace('.', ''))
            });
          }
        } catch (e) {
          Utils.handleNotAllowedUser();
        }
      }
      if (!response.ok) throw response;
      else {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          return text;
        }
      }
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  public static async googleAPI(url, method, body) {
    url = `${url}?key=${mapConfig.TOKEN_GOOGLE}`;

    const options = {
      method,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    };

    try {
      const response = await fetch(url, options);
      if (!response.ok) throw response;
      else {
        const text = await response.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          return text;
        }
      }
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  private static async knock(
    type: Types,
    path: string = '',
    body: any = {},
    method: Methods = Methods.POST,
    params?: Parameters,
    version?: string
  ): Promise<any> {

    if ((vuex.state as any).Global.show_not_allowed_user_dialog) {
      return;
    }

    const headers = {
      'Cache-Control': 'no-cache',
      ['Accept']: 'application/json, text/plain, */*'
    };

    params = Object.assign(
      {
        query: {},
        auth: 'user',
        serialize: false,
        apiURL: this.API_URL,
        dataMethod: DataMethods.OBJECT,
        contentType: 'application/json'
      } as Parameters,
      params
    );

    if (params.contentType) {
      headers['Content-Type'] = params.contentType;
    }

    // Add custom headers if provided
    if (params.headers) {
      Object.assign(headers, params.headers);
    }

    // JWT Authentication (preferred) or API Key fallback
    const user = store.get(params.auth) || {};
    if (params.auth && user.jwt_token) {
      headers['Authorization'] = `Bearer ${user.jwt_token}`;
    }

    if (params.serialize || [Methods.GET].indexOf(method) !== -1) {
      params.dataMethod = DataMethods.QUERY;
    }

    switch (params.dataMethod) {
      case DataMethods.OBJECT:
        body = body ? JSON.stringify(body) : null;
        break;

      case DataMethods.QUERY:
        body = this.serialize(body) as any;
        break;

      case DataMethods.FORMDATA:
        const formData = new FormData();
        for (const k in body) formData.append(k, body[k]);
        body = formData;
        break;
    }

    let url;
    if ([Methods.GET, Methods.DELETE, Methods.HEAD].indexOf(method) !== -1) {
      url = this.getAPI(type, path, params, body);
      body = null;
    } else {
      url = this.getAPI(type, path, params, this.serialize(params.query));
    }

    const v = version ? version : 'v3';

    try {
      const response = await fetch(params.apiURL + v + url, {
        method: method.toUpperCase(),
        body,
        headers
      });

      if (response && response.status === 403 && params.auth) {
        try {
          const payload = await response.clone().json();
          const detail = payload && payload.detail ? payload.detail : '';
          if (API.isAuthError(detail)) {
            Utils.handleNotAllowedUser();
          } else {
            vuex.dispatch('Global/throwNotify', {
              type: 'error',
              title: `${i18n.t('Error')}!`,
              text: i18n.t(payload.detail.replace('.', ''))
            });
          }
        } catch (e) {
          Utils.handleNotAllowedUser();
        }
      }

      if (!response.ok) throw response;
      else {
        const text = await response.text();
        try {
          if (text.length > 0) {
            return JSON.parse(text);
          } else return response;
        } catch (e) {
          return text;
        }
      }
    } catch (e) {
      if (url.includes('reporting_query')) {
        const bad_status = [400, 408, 502, 503, 504];
        if (bad_status.includes(e['status'])) {
          try {
            const response = await fetch(params.apiURL + v + url, {
              method: method.toUpperCase(),
              body,
              headers
            });
            if (!response.ok) throw response;
            else {
              const text = await response.text();
              try {
                if (text.length > 0) {
                  return JSON.parse(text);
                } else return;
              } catch (e) {
                return text;
              }
            }
          } catch (e) {
            console.log(e);
            throw e;
          }
        } else throw e;
      } else {
        console.log(e);
        throw e;
      }
    }
  }

  private static async knockStream(
    type: Types,
    path: string = '',
    body: any = {},
    params?: any,
    version?: string
  ): Promise<any> {
    if ((vuex.state as any).Global.show_not_allowed_user_dialog) {
      return;
    }

    const headers: Record<string, string> = {
      'Cache-Control': 'no-cache',
      'Accept': 'application/json, text/plain, */*',
      'Content-Type': 'application/json'
    };

    params = Object.assign(
      {
        auth: 'user',
        apiURL: this.API_URL
      },
      params
    );

    if (params.headers) {
      Object.assign(headers, params.headers);
    }

    const user = store.get(params.auth) || {};
    if (params.auth && user.jwt_token) {
      headers['Authorization'] = `Bearer ${user.jwt_token}`;
    }

    const url = this.getAPI(type, path, params);
    const v = version ? version : 'v3';
    const fullUrl = params.apiURL + v + url;
    const requestBody = body ? JSON.stringify(body) : null;

    const streamLabel = `[stream ${(body && body.where && JSON.stringify(body.where)) || path}]`;

    const doFetch = async (abortSignal: AbortSignal) => {
      const response = await fetch(fullUrl, {
        method: 'POST',
        body: requestBody,
        headers,
        signal: abortSignal
      });

      if (response && response.status === 403 && params.auth) {
        try {
          const payload = await response.clone().json();
          const detail = payload && payload.detail ? payload.detail : '';
          if (API.isAuthError(detail)) {
            Utils.handleNotAllowedUser();
          } else {
            vuex.dispatch('Global/throwNotify', {
              type: 'error',
              title: `${i18n.t('Error')}!`,
              text: i18n.t(payload.detail.replace('.', ''))
            });
          }
        } catch (e) {
          Utils.handleNotAllowedUser();
        }
      }

      if (!response.ok) throw response;
      return response;
    };

    const runOnce = async () => {
      const controller = new AbortController();
      const response = await doFetch(controller.signal);
      return await this.parseNDJSON(
        response,
        params.onBatch,
        params.batchSize,
        controller,
        streamLabel
      );
    };

    try {
      return await runOnce();
    } catch (e) {
      const bad_status = [400, 408, 502, 503, 504];
      const status = e && (e as any).status;
      const isTruncation = e && (e as any).__streamTruncated;
      if (isTruncation || (status && bad_status.includes(status))) {
        console.warn(`${streamLabel} retrying after`, isTruncation ? 'stream truncation' : `status ${status}`);
        try {
          return await runOnce();
        } catch (e2) {
          console.error(`${streamLabel} retry failed`, e2);
          throw e2;
        }
      }
      console.error(`${streamLabel} error`, e);
      throw e;
    }
  }

  // Fast main-thread yield using MessageChannel. Macrotask-style (so the
  // browser can paint and the network reader can pump between batches), but
  // without the ~4ms clamp that `setTimeout(0)` is subject to in browsers.
  // A single shared channel + FIFO callback queue keeps allocation cost zero
  // per yield.
  private static _yieldChannel: MessageChannel | null = null;
  private static _yieldCallbacks: Array<() => void> = [];
  private static yieldToMain(): Promise<void> {
    if (!API._yieldChannel) {
      API._yieldChannel = new MessageChannel();
      API._yieldChannel.port1.onmessage = () => {
        const cb = API._yieldCallbacks.shift();
        if (cb) cb();
      };
    }
    return new Promise<void>((resolve) => {
      API._yieldCallbacks.push(resolve);
      API._yieldChannel!.port2.postMessage(0);
    });
  }

  private static async parseNDJSON(
    response: Response,
    onBatch?: (batch: any[]) => void,
    batchSize?: number,
    controller?: AbortController,
    streamLabel: string = '[stream]'
  ): Promise<any[]> {
    const result: any[] = [];
    const pending: any[] = [];
    // Stall detector: if no bytes arrive for this long, treat the connection as
    // truncated (proxy/backend idle timeout) and throw so the caller can retry.
    const STALL_TIMEOUT_MS = 20000;
    const startTime = Date.now();
    let lastByteAt = startTime;
    let totalLines = 0;
    let totalBytes = 0;
    // Decouple commits from the read loop. The producer (network reader) pushes
    // batches into `commitQueue`; the consumer drains it asynchronously with yields,
    // so a slow Vuex commit cannot apply TCP backpressure on the server stream.
    const commitQueue: any[][] = [];
    let producerDone = false;
    const MAX_QUEUED = 4; // soft backpressure: never queue more than ~4 batches

    const consumer = (async () => {
      if (!onBatch) return;
      while (!producerDone || commitQueue.length > 0) {
        if (commitQueue.length === 0) {
          // Sub-ms macrotask yield (vs. setTimeout's ~4ms clamp) so the
          // consumer wakes promptly when the producer pushes the next batch.
          await API.yieldToMain();
          continue;
        }
        const batch = commitQueue.shift();
        try {
          onBatch(batch as any[]);
        } catch (err) {
          console.error('onBatch error', err);
        }
        // Yield after every commit so the network reader can pump more chunks
        // and the browser can paint / process events.
        await API.yieldToMain();
      }
    })();

    const flushPending = async (force: boolean) => {
      if (!onBatch || pending.length === 0) return;
      if (!force && batchSize && pending.length < batchSize) return;
      const batch = pending.splice(0, pending.length);
      commitQueue.push(batch);
      // Soft backpressure: if the consumer is far behind, wait briefly so the
      // commit queue cannot grow without bound on very fast networks.
      while (commitQueue.length > MAX_QUEUED) {
        await API.yieldToMain();
      }
    };

    const pushParsed = (obj: any) => {
      result.push(obj);
      if (onBatch) pending.push(obj);
      totalLines++;
    };

    if (!response.body || typeof (response.body as any).getReader !== 'function') {
      const text = await response.text();
      const lines = text.split('\n');
      for (const line of lines) {
        if (line.trim()) {
          try {
            pushParsed(JSON.parse(line));
          } catch (err) {
            console.error('NDJSON parse error', err, line);
          }
        }
      }
      await flushPending(true);
      producerDone = true;
      await consumer;
      return result;
    }

    const reader = (response.body as any).getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Stall watchdog: races against each reader.read(). If the network does not
    // deliver any bytes within STALL_TIMEOUT_MS, we abort the underlying fetch so
    // the read promise rejects and the caller can retry. This catches the case
    // where a proxy/backend closes the response mid-stream silently or hangs.
    let stallTimer: any = null;
    const armStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        const idleFor = Date.now() - lastByteAt;
        console.warn(
          `${streamLabel} stalled after ${totalLines} lines / ${totalBytes} bytes, idle for ${idleFor}ms — aborting`
        );
        try { if (controller) controller.abort(); } catch (e) { /* noop */ }
      }, STALL_TIMEOUT_MS);
    };
    const disarmStallTimer = () => {
      if (stallTimer) { clearTimeout(stallTimer); stallTimer = null; }
    };

    try {
      armStallTimer();
      while (true) {
        let readResult;
        try {
          readResult = await reader.read();
        } catch (err) {
          // Network error / abort. If we have already received some data, mark
          // this as a truncation so the caller can retry; otherwise rethrow.
          disarmStallTimer();
          if (totalLines > 0) {
            const truncErr: any = new Error(
              `${streamLabel} truncated after ${totalLines} lines (${totalBytes} bytes, ${Date.now() - startTime}ms): ${err && (err as any).message}`
            );
            truncErr.__streamTruncated = true;
            truncErr.cause = err;
            throw truncErr;
          }
          throw err;
        }
        const { done, value } = readResult;
        if (done) break;
        lastByteAt = Date.now();
        totalBytes += value ? value.byteLength : 0;
        armStallTimer();
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim()) {
            try {
              pushParsed(JSON.parse(line));
            } catch (err) {
              console.error(`${streamLabel} NDJSON parse error`, err, line);
            }
          }
        }
        await flushPending(false);
      }
    } finally {
      disarmStallTimer();
    }

    if (buffer.trim()) {
      try {
        pushParsed(JSON.parse(buffer));
      } catch (err) {
        console.error(`${streamLabel} NDJSON parse error`, err, buffer);
      }
    }
    await flushPending(true);
    producerDone = true;
    await consumer;

    const elapsed = Date.now() - startTime;
    console.info(
      `${streamLabel} done: ${totalLines} lines, ${totalBytes} bytes, ${elapsed}ms (${(totalBytes / 1024 / Math.max(elapsed, 1) * 1000).toFixed(0)} KB/s)`
    );

    return result;
  }

  private static serialize(obj): string {
    if (!obj) return '';

    const str: string[] = [];
    for (const p in obj)
      if (obj.hasOwnProperty(p))
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
    return str.join('&');
  }

  private static getAPI(type: Types, request, params, query?: string) {
    let itemID = '';
    switch (type) {
      case Types.COMPANIES:
        itemID = !params.hasOwnProperty('itemID') ? '' : params.itemID;
        break;

      case Types.PROJECTS:
      case Types.DEVICES:
        itemID = params.itemID;
        break;
    }

    let url = `/${type}/${itemID || ''}/${request}/${query ? '?' + query : ''}`;
    while (/\/\//.test(url)) {
      url = url.replace('//', '/');
    }

    return url;
  }
}
